import {
  TenantShareSetting,
  TenantShareSettingUpsertPayload,
} from "../../models/tenantShareSettings";
import { API_ENDPOINTS } from "../../utils/api-service";
import { api } from "./api";

const shareSettingTag = (id: string) => ({ type: "shareSettings" as const, id });
const shareSettingListTag = { type: "shareSettings" as const, id: "LIST" };

const unwrapApiData = (response: unknown): unknown => {
  if (typeof response !== "object" || response === null) return response;

  const record = response as Record<string, unknown>;
  return record.success === true && record.data !== undefined ? record.data : response;
};

const toNullableInt = (value: unknown): number | null =>
  value === null || value === undefined || value === "" ? null : Number(value);

const toNullableString = (value: unknown): string | null =>
  value === null || value === undefined || value === "" ? null : String(value);

const mapShareSetting = (raw: unknown): TenantShareSetting => {
  const row = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;

  return {
    id: toNullableString(row.id),
    tenantId: String(row.tenantId ?? ""),
    eventTypeId: toNullableInt(row.eventTypeId),
    eventTypeName: toNullableString(row.eventTypeName),
    eventSubTypeId: toNullableInt(row.eventSubTypeId),
    eventSubTypeName: toNullableString(row.eventSubTypeName),
    shareText: String(row.shareText ?? ""),
    isDefault: row.isDefault === true,
    createdAt: toNullableString(row.createdAt),
    updatedAt: toNullableString(row.updatedAt),
  };
};

const mapShareSettingList = (response: unknown): TenantShareSetting[] => {
  const data = unwrapApiData(response);
  return Array.isArray(data) ? data.map(mapShareSetting) : [];
};

export const tenantShareSettingsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getShareSettings: build.query<TenantShareSetting[], void>({
      query: () => ({ url: API_ENDPOINTS.SHARE_SETTINGS.BASE }),
      transformResponse: mapShareSettingList,
      providesTags: (result) => [
        ...(result ?? [])
          .filter((setting) => setting.id !== null)
          .map((setting) => shareSettingTag(setting.id as string)),
        shareSettingListTag,
      ],
    }),

    upsertShareSetting: build.mutation<TenantShareSetting, TenantShareSettingUpsertPayload>({
      query: (body) => ({
        url: API_ENDPOINTS.SHARE_SETTINGS.BASE,
        method: "PUT",
        body,
      }),
      transformResponse: (response: unknown) => mapShareSetting(unwrapApiData(response)),
      invalidatesTags: [shareSettingListTag],
    }),

    deleteShareSetting: build.mutation<void, string>({
      query: (id) => ({
        url: API_ENDPOINTS.SHARE_SETTINGS.BY_ID(id),
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [shareSettingTag(id), shareSettingListTag],
    }),
  }),
});

export const {
  useGetShareSettingsQuery,
  useUpsertShareSettingMutation,
  useDeleteShareSettingMutation,
} = tenantShareSettingsApi;
